import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import { ReportChartRow, BarCard, DonutCard, groupCount } from './ReportCharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v||'–'; } };

const STATUS_COLOR = {
  active:    'bg-green-50 text-green-700',
  completed: 'bg-blue-50 text-blue-700',
  review:    'bg-amber-50 text-amber-700',
  inactive:  'bg-slate-100 text-slate-500',
};

const ProgressBar = ({ value }) => {
  const pct = Math.min(100, Math.max(0, value||0));
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full', color)} style={{ width:`${pct}%` }}/>
      </div>
      <span className="text-xs text-slate-600 font-medium w-9 text-right">{pct}%</span>
    </div>
  );
};

const COLS = [
  { key:'client_name',        label:'Client' },
  { key:'plan_name',          label:'Plan Name' },
  { key:'status',             label:'Status',       render: v => <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize', STATUS_COLOR[v]||'bg-slate-100 text-slate-600')}>{v||'–'}</span> },
  { key:'start_date',         label:'Start Date',   render: fmtDate },
  { key:'end_date',           label:'Review Date',  render: fmtDate },
  { key:'plan_age_days',      label:'Age (days)' },
  { key:'num_goals',          label:'Goals' },
  { key:'completed_goals',    label:'Completed' },
  { key:'in_progress_goals',  label:'In Progress' },
  { key:'avg_progress',       label:'Avg Progress', render: v => <ProgressBar value={v} /> },
  { key:'highest_progress',   label:'Highest %',    render: v => v != null ? `${v}%` : '–' },
  { key:'lowest_progress',    label:'Lowest %',     render: v => v != null ? `${v}%` : '–' },
];

export default function CarePlanProgress() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]     = useState(format(new Date(), 'yyyy-MM-dd'));
  const pagination = usePagination(data);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/care-plan-progress?start_date=${startDate}&end_date=${endDate}`)
      .then(r => { setData(r.data||[]); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const chips = [{ label:'Date Range', value:`${startDate} - ${endDate}` }];

  return (
    <ReportLayout title="Care Plan Progress" data={data} filename="care_plan_progress" columns={COLS}
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      chart={
        <ReportChartRow>
          <BarCard title="Plans by progress" data={[{ name: '<50%', value: data.filter(r => (+r.avg_progress || 0) < 50).length }, { name: '50–79%', value: data.filter(r => { const p = +r.avg_progress || 0; return p >= 50 && p < 80; }).length }, { name: '80%+', value: data.filter(r => (+r.avg_progress || 0) >= 80).length }]} color="#4F46E5" />
          <DonutCard title="Plans by status" data={groupCount(data, 'status')} />
        </ReportChartRow>
      }
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}/>
    </ReportLayout>
  );
}
